import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { contactInfo, logoUrl } from '../data/mock';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        source: 'contact_form'
      });
      toast.success('Thank you for contacting us! We will get back to you soon.');
      setFormData({ firstName: '', lastName: '', phone: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookConsultation = () => {
    const subject = encodeURIComponent('Consultation Request - Jay Defence Academy');
    const body = encodeURIComponent('Hello,\n\nI would like to book a consultation regarding your defence preparation programs.\n\nPlease contact me at your earliest convenience.\n\nThank you.');
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  const contactInfoItems = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['2nd Floor, Yeri Classes', 'Vidyagiri, Dharwad', 'Karnataka, India']
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: [contactInfo.phone],
      link: `tel:${contactInfo.phone.replace(/\s/g, '')}`
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: [contactInfo.email],
      link: `mailto:${contactInfo.email}`
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: ['Monday - Saturday', '9:00 AM - 6:00 PM']
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800/90 to-olive-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-gold-400 font-semibold text-sm tracking-wider uppercase mb-4">Get In Touch</span>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            Contact <span className="text-gold-400">Us</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We would love to help you become an officer for the Indian Armed Forces!
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-cream-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfoItems.map((info, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-navy-900 flex items-center justify-center mb-4">
                    <info.icon className="w-6 h-6 text-gold-400" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-900 mb-3">{info.title}</h3>
                  {info.link ? (
                    <a href={info.link} className="block">
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-gray-600 text-sm hover:text-gold-600 transition-colors">{detail}</p>
                      ))}
                    </a>
                  ) : (
                    info.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                    ))
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Content */}
            <div>
              <span className="text-olive-600 font-semibold text-sm tracking-wider uppercase">Send Us A Message</span>
              <h2 className="text-4xl font-bold text-navy-900 mt-3 mb-6">We're Here to Help</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Have questions about our courses, admission process, or anything else? 
                Fill out the form and our team will get back to you within 24 hours.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Expert guidance for SSB preparation',
                  'Personalized course recommendations',
                  'Information about accommodation facilities',
                  'Scholarship and financial assistance queries'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-olive-600" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              
              {/* Quick Action Buttons */}
              <div className="space-y-4 mb-8">
                <Button 
                  onClick={handleBookConsultation}
                  className="w-full bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold py-6"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Book a Consultation Today
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="block">
                  <Button variant="outline" className="w-full border-2 border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white font-bold py-6">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Us: 7204064650
                  </Button>
                </a>
              </div>
              
              {/* Google Map */}
              <div className="bg-navy-900 rounded-2xl p-6 text-white">
                <h4 className="font-semibold text-gold-400 mb-4">Find Us On Map</h4>
                <div className="rounded-xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3845.7862366749!2d75.01408!3d15.4399169!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bb8d396f5aea011%3A0x8b71f5d1f8495aef!2sJay%20Defence%20Academy!5e0!3m2!1sen!2sin!4v1702000000000!5m2!1sen!2sin"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Jay Defence Academy Location"
                    className="rounded-xl"
                  />
                </div>
                <a
                  href="https://www.google.com/maps/dir//2nd+floor,+of+Yeri+classes,+Jay+Defence+Academy,+Rajatagiri,+Vidyagiri,+Dharwad,+Karnataka+580004/@18.5160028,73.8301434,15z/data=!3m1!4b1!4m8!4m7!1m0!1m5!1m1!1s0x3bb8d396f5aea011:0x8b71f5d1f8495aef!2m2!1d75.0166584!2d15.4399169?entry=ttu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-gold-400 hover:text-gold-300 transition-colors font-medium"
                >
                  <MapPin className="w-4 h-4" />
                  Get Directions on Google Maps
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
            
            {/* Contact Form */}
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-navy-900 font-medium">First Name *</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        placeholder="John"
                        required
                        className="border-gray-200 focus:border-gold-500 focus:ring-gold-500 h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-navy-900 font-medium">Last Name *</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        placeholder="Doe"
                        required
                        className="border-gray-200 focus:border-gold-500 focus:ring-gold-500 h-12"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-navy-900 font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+91 98765 43210"
                      required
                      className="border-gray-200 focus:border-gold-500 focus:ring-gold-500 h-12"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-navy-900 font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      required
                      className="border-gray-200 focus:border-gold-500 focus:ring-gold-500 h-12"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-navy-900 font-medium">Your Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Tell us about your defence career aspirations, questions about courses, or any other queries..."
                      rows={5}
                      className="border-gray-200 focus:border-gold-500 focus:ring-gold-500 resize-none"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-navy-900 hover:bg-navy-800 text-white font-bold py-6 text-lg transition-all duration-300"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
